import { forwardRef, type ButtonHTMLAttributes, type HTMLAttributes, type ReactNode } from 'react'
import { clsx } from 'clsx'
import { FlaskConical } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { PriceKind } from '@globalx/core'
import { useFormat } from '@/lib/format'

type ButtonVariant = 'gold' | 'ghost' | 'outline' | 'soft'

const BUTTON_STYLES: Record<ButtonVariant, string> = {
  gold: 'gold-fill shadow-gold hover:brightness-105 active:brightness-95',
  ghost: 'text-muted hover:text-fg hover:bg-surface-2',
  outline: 'border border-line-strong text-fg hover:bg-surface-2',
  soft: 'bg-surface-2 text-fg hover:bg-surface-3 border border-line',
}

export const Button = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; size?: 'sm' | 'md' | 'lg' }
>(function Button({ variant = 'soft', size = 'md', className, ...props }, ref) {
  return (
    <button
      ref={ref}
      className={clsx(
        'inline-flex items-center justify-center gap-2 rounded-xl font-semibold whitespace-nowrap transition duration-200 ease-out-soft disabled:opacity-50',
        size === 'sm' && 'h-9 px-3 text-sm',
        size === 'md' && 'h-11 px-4 text-sm',
        size === 'lg' && 'h-12 px-6 text-base',
        BUTTON_STYLES[variant],
        className,
      )}
      {...props}
    />
  )
})

export const IconButton = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { label: string }
>(function IconButton({ label, className, children, ...props }, ref) {
  return (
    <button
      ref={ref}
      aria-label={label}
      title={label}
      className={clsx(
        'inline-flex size-10 shrink-0 items-center justify-center rounded-xl text-muted transition hover:bg-surface-2 hover:text-fg',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  )
})

export function Card({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLElement> & { children: ReactNode }) {
  return (
    <section className={clsx('card', className)} {...props}>
      {children}
    </section>
  )
}

export function SectionHeader({
  title,
  action,
  badge,
  className,
}: {
  title: ReactNode
  action?: ReactNode
  badge?: ReactNode
  className?: string
}) {
  return (
    <div className={clsx('flex items-center justify-between gap-3', className)}>
      <div className="flex min-w-0 items-center gap-2">
        <h2 className="truncate text-base font-semibold tracking-tight">{title}</h2>
        {badge}
      </div>
      {action}
    </div>
  )
}

export function DemoBadge({ className }: { className?: string }) {
  const { t } = useTranslation()
  return (
    <span
      title={t('common.demoData')}
      className={clsx(
        'inline-flex shrink-0 items-center gap-1 rounded-full bg-demo-soft px-2 py-0.5 text-[0.7rem] font-semibold text-demo ring-1 ring-demo/25',
        className,
      )}
    >
      <FlaskConical className="size-3" aria-hidden />
      {t('common.demo')}
    </span>
  )
}

const KIND_STYLES: Record<PriceKind, string> = {
  demo: 'bg-demo-soft text-demo ring-demo/25',
  reference: 'bg-surface-2 text-muted ring-line-strong',
  delayed: 'bg-warn-soft text-warn ring-warn/25',
  live: 'bg-up-soft text-up ring-up/25',
}

export function KindBadge({ kind }: { kind: PriceKind }) {
  const { t } = useTranslation()
  return (
    <span
      title={t(`converter.kindHint.${kind}`)}
      className={clsx('inline-flex items-center rounded-full px-2 py-0.5 text-[0.7rem] font-semibold ring-1', KIND_STYLES[kind])}
    >
      {t(`converter.kind.${kind}`)}
    </span>
  )
}

export function ChangePill({ value, className }: { value: number; className?: string }) {
  const { percent } = useFormat()
  const up = value > 0
  const down = value < 0
  return (
    <span
      className={clsx(
        'num ltr inline-flex items-center rounded-lg px-1.5 py-0.5 text-xs font-semibold',
        up && 'bg-up-soft text-up',
        down && 'bg-down-soft text-down',
        !up && !down && 'bg-surface-2 text-muted',
        className,
      )}
    >
      {up ? '▲ ' : down ? '▼ ' : ''}
      {percent(value)}
    </span>
  )
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
  className,
  size = 'md',
}: {
  value: T
  options: { value: T; label: ReactNode }[]
  onChange: (v: T) => void
  label: string
  className?: string
  size?: 'sm' | 'md'
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={clsx('inline-flex rounded-xl bg-surface-2 p-1 ring-1 ring-line', className)}
    >
      {options.map((o) => {
        const active = o.value === value
        return (
          <button
            key={o.value}
            role="radio"
            aria-checked={active}
            onClick={() => onChange(o.value)}
            className={clsx(
              'flex-1 rounded-lg font-semibold whitespace-nowrap transition duration-200',
              size === 'sm' ? 'h-7 px-2.5 text-xs' : 'h-9 px-3.5 text-sm',
              active ? 'gold-fill shadow-gold' : 'text-muted hover:text-fg',
            )}
          >
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

export function AdSlot({ className }: { className?: string }) {
  const { t } = useTranslation()
  // Reserved size prevents layout shift once an ad network is approved (D8).
  return (
    <aside
      aria-label={t('common.ad')}
      className={clsx(
        'relative flex min-h-[90px] items-center justify-center rounded-2xl border border-dashed border-line-strong bg-surface-0/60 text-xs text-subtle',
        className,
      )}
    >
      <span className="absolute top-2 start-3 text-[0.65rem] font-semibold tracking-wide uppercase">
        {t('common.ad')}
      </span>
      {t('common.adSlot')}
    </aside>
  )
}

export function Skeleton({ className }: { className?: string }) {
  return (
    <span
      className={clsx(
        'relative block overflow-hidden rounded-lg bg-surface-2 before:absolute before:inset-0 before:-translate-x-full before:animate-[gx-shimmer_1.4s_infinite] before:bg-gradient-to-r before:from-transparent before:via-white/5 before:to-transparent',
        className,
      )}
    />
  )
}
